// Where a recorded take's audio goes, by room mode.
//
// CONNECTED — the jam has a pool. The WAV goes up at once
// (lib/pool-audio.ts: hashed, streamed to the pool, verified) and the clip names the pool key it comes back
// under, the same as audio every other client adds.
//
// LOCAL — there is no jam until a save makes one. The WAV stays in this tab as
// a blob, the clip names a `local/…` key (lib/recording.ts), and the player
// fetches it through an object URL. PublishRoom uploads whatever the room
// still points at once the save has a jam to put it in. Closing the tab loses
// the takes along with every other unsaved edit — the unsaved guard asks first.

import { useCallback, useEffect, useMemo, useRef } from "react";
import { putPoolAudio } from "@/lib/pool-audio";
import { isLocalTakeKey, mintLocalTakeKey } from "@/lib/recording";

export interface TakeStore {
  /** Keep a take's WAV bytes; resolves to the key a clip should name. */
  put: (wav: ArrayBuffer) => Promise<string>;
  /** The object URL for a take held in this tab; undefined for anything else. */
  urlOf: (key: string) => string | undefined;
  /** Every take held in this tab, by key — what a local save uploads. */
  held: ReadonlyMap<string, Blob>;
}

export function useTakeStore(
  jamId: string | undefined,
  /** Takes this tab held before (a draft restored after a reload). Read once. */
  initial?: ReadonlyArray<{ key: string; blob: Blob }>,
): TakeStore {
  const blobs = useRef<Map<string, Blob> | null>(null);
  const urls = useRef(new Map<string, string>());
  if (blobs.current === null) {
    blobs.current = new Map();
    for (const { key, blob } of initial ?? []) if (isLocalTakeKey(key)) blobs.current.set(key, blob);
  }
  const held = blobs.current;

  // The URLs outlive a render but not the page.
  useEffect(() => {
    const held = urls.current;
    return () => {
      for (const url of held.values()) URL.revokeObjectURL(url);
      held.clear();
    };
  }, []);

  const put = useCallback(
    async (wav: ArrayBuffer): Promise<string> => {
      if (jamId) {
        const up = await putPoolAudio(jamId, new Uint8Array(wav));
        return up.key;
      }
      const key = mintLocalTakeKey();
      const blob = new Blob([wav], { type: "audio/wav" });
      held.set(key, blob);
      urls.current.set(key, URL.createObjectURL(blob));
      return key;
    },
    [jamId, held],
  );

  // Minted on demand for a held take that has none: a restored draft's, or
  // one whose URL a remount's cleanup revoked.
  const urlOf = useCallback(
    (key: string) => {
      if (!isLocalTakeKey(key)) return undefined;
      let url = urls.current.get(key);
      const blob = held.get(key);
      if (!url && blob) {
        url = URL.createObjectURL(blob);
        urls.current.set(key, url);
      }
      return url;
    },
    [held],
  );

  return useMemo(() => ({ put, urlOf, held }), [put, urlOf, held]);
}
